import { z } from 'zod';

import { SETTINGS_GROUPS } from '@ekhodealer/ekho-common';
import { Box, Form, Note, Tag } from '@ekhodealer/ekho-common/rev';

import type { Section } from '@ekhodealer/ekho-common';

import { SettingsSection } from '@common/components/SettingsSection';

import { useServiceProgramInfo } from '../../hooks/useServiceProgramInfo';
import { getSectionGroup } from '../../lib/sectionGroupUtils';

/**
 * Derive a human-readable title from a section ID
 */
function getSectionTitle(section: Section): string {
	// Remove prefix and convert to title case
	const withoutPrefix = section.replace(
		/^(PROGRAM_|DOMAINS_|INTERNAL_|OPERATIONS_|INVENTORY_)/,
		'',
	);
	return withoutPrefix
		.split('_')
		.map((word) => word.charAt(0) + word.slice(1).toLowerCase())
		.join(' ');
}

/**
 * Default section component for sections that don't have a dedicated service form yet.
 * Always shows "Coming Soon" content and hides the toggle.
 */
export function DefaultSection({ section }: { section: Section }) {
	const programInfo = useServiceProgramInfo(section);
	const group = getSectionGroup(section) ?? SETTINGS_GROUPS.OPERATIONS;

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Minimal schema for sections without dedicated forms
	const defaultSchema = z.object({
		supported: z.boolean().optional(),
	});

	const handleSubmit = async (): Promise<{ isSuccess: boolean }> => {
		// No-op for default sections without forms
		return { isSuccess: true };
	};

	const title = getSectionTitle(section);

	return (
		<SettingsSection
			title={title}
			tag={<Tag size='small' accent='neutral' variant='pill' indicator={false}>Coming soon</Tag>}
			hideDivider>
			<Box padding='24' background='secondary' rounding='12' id={section}>
				<Form
					name={`${group}-${section}`}
					schema={defaultSchema}
					defaultValues={{ supported: false }}
					onSubmit={handleSubmit}
					toggle={{ fieldName: 'supported', visibility: 'hidden' }}
					titleLevel='subgroup'
					showSaveAndClear>
					<Note>
						This setting is currently managed by Ekho. Contact your account
						manager to make changes.
					</Note>
				</Form>
			</Box>
		</SettingsSection>
	);
}
